import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./home.css";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import Contact from "./Contact";
import { SERVICE_ICONS } from "../../../Constants/Home";
import { APPS } from "../../../Constants/Apps";
import { APPS_CONTENT } from "../../../Constants/AppsContent";
import { useDocumentTitle } from "../../../Hooks/useDocumentTitle";
import { useTranslation } from "../../../Hooks/useTranslation";
import { useReveal } from "../../../Hooks/useReveal";
import { useActiveSection } from "../../../Hooks/useActiveSection";

const Home = () => {
  const { t, language } = useTranslation();
  useDocumentTitle();
  useReveal();
  const active = useActiveSection(["inicio", "contacto"]);
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showFloatingCta = pastHero && active !== "contacto";

  return (
    <>
      <Navbar />
      <main>
        <section id="inicio" className="hero">
          <div className="hero-bg" aria-hidden="true">
            <div className="hero-grid" />
            <div className="hero-glow" />
            <div className="hero-glow hero-glow-2" />
          </div>
          <div className="container hero-content">
            <span className="hero-badge hero-anim" style={{ "--d": 0 }}>
              <span className="pulse-dot" />
              {t.hero.badge}
            </span>
            <h1 className="hero-anim" style={{ "--d": 1 }}>
              {t.hero.titleStart}
              <span className="accent">{t.hero.titleAccent}</span>
              {t.hero.titleEnd}
            </h1>
            <p className="hero-text hero-anim" style={{ "--d": 2 }}>
              {t.hero.text}
            </p>
            <div className="hero-actions hero-anim" style={{ "--d": 3 }}>
              <a href="#proyectos" className="btn btn-fill">{t.hero.primary}</a>
              <a href="#contacto" className="btn btn-border">{t.hero.secondary}</a>
            </div>
          </div>
          <a href="#nosotros" className="scroll-cue" aria-label={t.hero.scroll}>
            <span />
          </a>
        </section>

        <section id="nosotros" className="section">
          <div className="container">
            <div data-reveal>
              <span className="eyebrow">{t.about.eyebrow}</span>
              <h2>{t.about.title}</h2>
              <p className="section-text">{t.about.text}</p>
            </div>
            <div className="grid grid-3 values">
              {t.about.values.map((value, index) => (
                <div key={index} className="card value-card" data-reveal style={{ "--d": index }}>
                  <span className="value-index">0{index + 1}</span>
                  <h3>{value.title}</h3>
                  <p>{value.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="servicios" className="section">
          <div className="container">
            <div data-reveal>
              <span className="eyebrow">{t.services.eyebrow}</span>
              <h2>{t.services.title}</h2>
              <p className="swipe-hint" aria-hidden="true">{t.services.swipe}</p>
            </div>
            <div className="grid grid-4 services" data-reveal>
              {t.services.items.map((service, index) => (
                <div key={index} className="card service-card">
                  <span className="card-icon" aria-hidden="true">{SERVICE_ICONS[index]}</span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="proyectos" className="section">
          <div className="container">
            <div data-reveal>
              <span className="eyebrow">{t.projects.eyebrow}</span>
              <h2>{t.projects.title}</h2>
            </div>
            <div className="projects">
              {APPS.map((app, index) => {
                const content = APPS_CONTENT[language][app.id];
                return (
                  <article
                    key={app.id}
                    className="project"
                    data-reveal
                    style={{ "--d": index, "--app-accent": app.accent }}
                  >
                    <Link to={`/apps/${app.id}`} className="project-icon-wrap">
                      <img className="project-icon" src={app.icon} alt={app.name} />
                    </Link>
                    <div className="project-body">
                      <h3>
                        <Link to={`/apps/${app.id}`}>{app.name}</Link>
                      </h3>
                      <p className="project-tagline">{content.tagline}</p>
                      <p>{content.description}</p>
                      <div className="tags">
                        {app.tags.map((tag) => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                        {!app.storeLink && !app.playStoreLink && <span className="tag tag-soon">{t.projects.comingSoon}</span>}
                      </div>
                      <div className="project-actions">
                        <Link className="btn btn-fill" to={`/apps/${app.id}`}>
                          {t.projects.view}
                        </Link>
                        {app.storeLink && (
                          <a
                            className="btn btn-border"
                            href={app.storeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t.projects.download}
                          </a>
                        )}
                        {app.playStoreLink && (
                          <a
                            className="btn btn-border"
                            href={app.playStoreLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t.projects.downloadPlay}
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
              <article className="project project-next" data-reveal style={{ "--d": APPS.length }}>
                <div className="project-body">
                  <h3>{t.projects.next.title}</h3>
                  <p>{t.projects.next.text}</p>
                  <a className="btn btn-border" href="#contacto">{t.projects.next.cta}</a>
                </div>
              </article>
            </div>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />

      <a
        href="#contacto"
        className={`floating-cta ${showFloatingCta ? "show" : ""}`}
        aria-hidden={!showFloatingCta}
        tabIndex={showFloatingCta ? 0 : -1}
      >
        <span className="pulse-dot" />
        {t.nav.cta}
      </a>
    </>
  );
};

export default Home;
